import { useMemo, useState } from "react";
import { AlertCircle, Loader2, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useCustomerMonthlyTotals } from "@/hooks/useCustomerMonthlyTotals";

type SortKey = "amount" | "count" | "nickname";

const SORT_OPTIONS: readonly { key: SortKey; label: string }[] = [
  { key: "amount", label: "금액순" },
  { key: "count", label: "건수순" },
  { key: "nickname", label: "닉네임순" },
];

/** 'YYYY-MM-01' → '9월' (토글용, 연속 3개월이라 연도 생략해도 모호하지 않다) */
function shortMonthLabel(month: string): string {
  return `${Number(month.slice(5, 7))}월`;
}

/** 'YYYY-MM-01' → '2026년 9월' (요약 줄용) */
function fullMonthLabel(month: string): string {
  return `${month.slice(0, 4)}년 ${Number(month.slice(5, 7))}월`;
}

// 본문 — 요금제 게이트를 통과했을 때만 마운트된다. 훅 호출도 여기서만 일어나므로
// 무료 사용자에게는 RPC가 아예 실행되지 않는다.
function CustomerMonthlyTotalsContent() {
  const { totals, isLoading, error, months, month, loadedMonth, setMonth, refetch } = useCustomerMonthlyTotals();
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("amount");

  const rows = useMemo(() => {
    const source = totals?.customers ?? [];
    const q = query.trim();
    const filtered = q ? source.filter((row) => row.nickname.includes(q)) : source;
    // 서버가 이미 금액 내림차순으로 보내므로 금액순은 재정렬하지 않는다.
    if (sortKey === "amount") return filtered;
    const sorted = [...filtered];
    if (sortKey === "count") {
      sorted.sort((a, b) => b.order_count - a.order_count || a.nickname.localeCompare(b.nickname, "ko"));
    } else {
      sorted.sort((a, b) => a.nickname.localeCompare(b.nickname, "ko"));
    }
    return sorted;
  }, [totals, query, sortKey]);

  // 숫자 라벨은 선택 월이 아니라 실제 로드된 월을 쓴다 — 토글 직후 stale 데이터가
  // 새 기간으로 오표기되는 것을 막는다.
  const labelMonth = loadedMonth ?? month;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="space-y-1.5">
            <CardTitle>고객별 구매 총액</CardTitle>
            <CardDescription>
              입금 완료와 출고 완료 주문의 상품 금액 합계입니다.
              <br />
              배송비는 포함되지 않으며 킵 주문은 포함됩니다.
            </CardDescription>
          </div>
          {/* 주문 현황 요약의 기간 토글과 동일한 문법(gray-100 트랙 + 흰 필).
              shadcn Button의 secondary/ghost는 이 레포 tailwind.config에 시맨틱 토큰이 없어 죽은 클래스다. */}
          <div
            role="group"
            aria-label="조회 월 선택"
            className="inline-flex shrink-0 items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
          >
            {months.map((option) => {
              const isActive = option === month;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={isActive}
                  disabled={isLoading && !isActive}
                  onClick={() => { if (!isActive) setMonth(option); }}
                  className={cn(
                    "inline-flex h-7 min-w-[3rem] items-center justify-center rounded-md px-2.5",
                    "text-xs font-medium tabular-nums transition-colors duration-150",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2",
                    "disabled:pointer-events-none disabled:opacity-50",
                    isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900",
                  )}
                >
                  {shortMonthLabel(option)}
                </button>
              );
            })}
          </div>
        </div>
      </CardHeader>

      <CardContent aria-busy={isLoading}>
        {!totals && !error ? (
          // 최초 로드에만 스피너 — 이후 월 전환 refetch는 stale 콘텐츠 유지 + dim (레이아웃 점프 방지)
          <div className="flex min-h-[200px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : error && !totals ? (
          <div className="flex flex-col items-start gap-2">
            {/* 원인은 logError로 기록한다. 화면에는 지역화되지 않은 Error.message를 섞지 않는다. */}
            <p className="text-red-500">구매 총액을 불러오지 못했습니다.</p>
            <Button variant="outline" size="sm" onClick={refetch}>다시 시도</Button>
          </div>
        ) : totals ? (
          <>
            {error && (
              // refetch 실패 — 이전 월의 stale 데이터는 유지한 채 인라인으로 알린다
              <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-sm text-red-600">구매 총액을 불러오지 못했습니다. 이전 데이터가 표시 중입니다.</p>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={refetch}>다시 시도</Button>
              </div>
            )}

            <div className={cn("transition-opacity duration-200", isLoading && "pointer-events-none opacity-50")}>
              {/* 요약 숫자는 서버 캡 이전 전량 기준 — 잘린 목록의 합계를 보여주면 안 된다 */}
              <p className="text-sm text-gray-700">
                <span className="font-medium">{fullMonthLabel(labelMonth)}</span>
                {" · 고객 "}
                <span className="font-medium tabular-nums">{totals.customer_count.toLocaleString()}명</span>
                {" · 합계 "}
                <span className="font-medium tabular-nums">{totals.total_amount.toLocaleString()}원</span>
              </p>

              {totals.is_partial && (
                <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  고객이 많아 구매금액 기준 상위 {totals.cap.toLocaleString()}명만 표시합니다.
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                <div className="relative w-full max-w-xs">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="닉네임 검색..."
                    aria-label="닉네임 검색"
                    className="pl-8"
                  />
                </div>
                <div
                  role="group"
                  aria-label="정렬 기준 선택"
                  className="inline-flex shrink-0 items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
                >
                  {SORT_OPTIONS.map(({ key, label }) => {
                    const isActive = key === sortKey;
                    return (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => { if (!isActive) setSortKey(key); }}
                        className={cn(
                          "inline-flex h-7 items-center justify-center rounded-md px-2.5",
                          "text-xs font-medium transition-colors duration-150",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2",
                          isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900",
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {totals.customer_count === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">
                  이 달에는 입금 완료나 출고 완료 주문이 없습니다.
                </p>
              ) : rows.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">검색 결과가 없습니다.</p>
              ) : (
                <div className="mt-3 max-h-[28rem] overflow-y-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>닉네임</TableHead>
                        <TableHead className="w-24 text-right">건수</TableHead>
                        <TableHead className="w-36 text-right">금액</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.nickname}>
                          <TableCell className="font-medium text-gray-800">{row.nickname}</TableCell>
                          <TableCell className="text-right tabular-nums text-gray-600">
                            {row.order_count.toLocaleString()}건
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-gray-800">
                            {row.total_amount.toLocaleString()}원
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function UpgradeNotice() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>고객별 구매 총액</CardTitle>
        <CardDescription>월별로 고객이 얼마씩 구매했는지 한눈에 확인합니다.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
          <p className="text-sm text-amber-800">
            고객별 구매 총액은 스탠다드 플랜 이상부터 제공됩니다.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * 대시보드 고객별 월 구매 총액 카드. props 0개 자기완결.
 *
 * 요금제 게이트를 블러가 아니라 마운트 분기로 둔 이유: /today-sales의 BlurredSectionWrapper는
 * 데이터를 받아온 뒤 CSS로 가리는 구조라 무료 사용자에 대해서도 RPC가 실행되고 응답이
 * 네트워크로 내려간다. 마운트 게이트는 데이터가 애초에 나가지 않는다.
 * 구독 로딩 중에는 free로 간주하지 않는다 — 업셀이 깜빡 보였다 사라지는 것을 막는다.
 */
export default function CustomerMonthlyTotalsCard() {
  const { subscriptionPlan, isLoading: subscriptionLoading } = useSubscription();

  if (subscriptionLoading) return null;

  // subscriptionPlan === null은 "무료"가 아니라 "아직 모름"이다 — 이 앱은 구독이 없는 계정에
  // 무료 체험 구독을 만들어 주므로(SubscriptionContext) 실제 무료 사용자는 id가 'free'인 플랜을 갖는다.
  // 로그아웃→로그인 첫 프레임은 isLoading=false + plan=null이라, 여기서 업셀을 그리면
  // 유료 셀러에게 "스탠다드 플랜 이상" 안내가 깜빡 보인다. 미확정 구간은 아무것도 그리지 않는다.
  // RPC도 실행되지 않으므로 이 판정은 fail-closed다.
  if (!subscriptionPlan) return null;

  // 정책은 /today-sales의 PlanFeatures.canViewMonthlySales(월별 매출)와 동급이다.
  // 그 상수가 TodaySales 모듈 로컬이라 페이지 파일을 건드리지 않으려고 여기서 동일 판정을 둔다.
  const planId = subscriptionPlan.id;
  if (planId !== "standard" && planId !== "premium") return <UpgradeNotice />;

  return <CustomerMonthlyTotalsContent />;
}
